'use client';

import { useRef, useState } from 'react';
import type { JSX } from 'react';
import { ChevronDown } from 'lucide-react';

interface FAQItemProps {
  question: string;
  answer: JSX.Element;
}

const faqList: FAQItemProps[] = [
  {
    question: 'How do students join?',
    answer: (
      <div className="space-y-2 leading-relaxed">
        Students can join a quiz at{' '}
        <a
          href="https://quizwhizzer.com/play"
          className="text-sky-600 hover:text-sky-700 underline underline-offset-2"
          target="_blank"
        >
          quizwhizzer.com/play
        </a>
        . Simply enter the unique code provided by the game host and then enter
        a nickname. No account required!
      </div>
    ),
  },
  {
    question: 'How many players in a game?',
    answer: (
      <div>
        <p>
          Up to 30 players can join a game on the free plan. If you need to
          support more than this, the Pro plan allows up to 100 players.
        </p>
      </div>
    ),
  },
  {
    question: 'How easy is it to set up a quiz on QuizWhizzer?',
    answer: (
      <div>
        <p>
          QuizWhizzer is designed to be user-friendly and intuitive. Most
          teachers can set up a quiz in just a few minutes with our
          straightforward interface. Plus, you can save questions from
          ready-made quizzes to your personal question bank, making future quiz
          creation even faster!
        </p>
      </div>
    ),
  },
  {
    question: 'Can I use QuizWhizzer for any subject?',
    answer: (
      <div>
        <p>
          Yes. QuizWhizzer is adaptable for any subject and grade level.
          Whether you&apos;re teaching math, science, history, or language arts,
          you can customize quizzes to fit your curriculum needs.
        </p>
      </div>
    ),
  },
  {
    question: "Is my students' data safe on QuizWhizzer?",
    answer: (
      <div>
        <p>
          Absolutely! Data privacy and security are top priorities for us. With
          QuizWhizzer, students don&apos;t need to sign up or share personal
          information to join games. Just a username is enough. This ensures a safe
          and anonymous experience, keeping student data secure while they enjoy
          interactive learning.
        </p>
      </div>
    ),
  },
  {
    question: 'I have another question',
    answer: (
      <div className="space-y-2 leading-relaxed">
        Get in touch at{' '}
        <a
          href="mailto:support@quizwhizzer.com"
          className="text-sky-600 hover:text-sky-700 underline underline-offset-2"
        >
          support@quizwhizzer.com
        </a>
        . Typical response time 1 working day.
      </div>
    ),
  },
];

const FaqItem = ({ item }: { item: FAQItemProps }) => {
  const accordion = useRef<HTMLDivElement>(null);
  const [isOpen, setIsOpen] = useState(false);

  return (
    <li className="border-b border-ink-900/15 last:border-0">
      <button
        className="relative flex gap-3 items-center w-full py-5 text-left"
        onClick={(e) => {
          e.preventDefault();
          setIsOpen(!isOpen);
        }}
        aria-expanded={isOpen}
      >
        <span
          className={`flex-1 font-display font-semibold text-lg md:text-xl ${
            isOpen ? 'text-brand-600' : 'text-ink-900'
          }`}
        >
          {item?.question}
        </span>
        <ChevronDown
          className={`w-5 h-5 text-ink-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
        />
      </button>

      <div
        ref={accordion}
        className="transition-all duration-300 ease-in-out overflow-hidden"
        style={
          isOpen
            ? { maxHeight: accordion?.current?.scrollHeight, opacity: 1 }
            : { maxHeight: 0, opacity: 0 }
        }
      >
        <div className="pb-5 text-ink-600 leading-relaxed">
          {item?.answer}
        </div>
      </div>
    </li>
  );
};

const FAQ = () => {
  return (
    <section className="bg-paper-100 paper-grain" id="faq">
      <div className="py-20 lg:py-28 px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
          <div className="flex flex-col text-left lg:basis-1/3">
            <p className="font-hand text-brand-600 text-2xl mb-3 -rotate-2 inline-block">
              while you&apos;re here
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl tracking-[-0.02em] text-ink-900 leading-[1.02]">
              Questions,
              <br />
              <span className="italic font-normal">answered.</span>
            </h2>
          </div>

          <ul className="lg:basis-2/3">
            {faqList.map((item, i) => (
              <FaqItem key={i} item={item} />
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
